import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth.jsx';

// Lazy load components for code splitting
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Predictive = React.lazy(() => import('./pages/Predictive'));
const ValidationRedirect = React.lazy(() => import('./pages/ValidationRedirect'));
const Validate = React.lazy(() => import('./pages/Validate'));
const ValidationActivity = React.lazy(() => import('./pages/ValidationActivity'));
const Submissions = React.lazy(() => import('./pages/Submissions'));
const Settings = React.lazy(() => import('./pages/Settings'));
const SignIn = React.lazy(() => import('./pages/SignIn'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const AdminLogin = React.lazy(() => import('./pages/AdminLogin'));

// Layout components
import Header from './components/layout/Header';
import Sidebar from './components/layout/Sidebar';
import LoadingSpinner from './components/common/LoadingSpinner';

function AppShell() {
  const { user, isLoading, signOut, stopImpersonation, impersonation } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [welcomeName, setWelcomeName] = useState(null);

  useEffect(() => {
    if (user) {
      const stored = sessionStorage.getItem('welcomeName');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (!parsed.expiresAt || parsed.expiresAt > Date.now()) {
            setWelcomeName(parsed.message);
            setTimeout(() => setWelcomeName(null), 4000);
          }
          sessionStorage.removeItem('welcomeName');
        } catch (err) {
          sessionStorage.removeItem('welcomeName');
        }
      }
    } else {
      setWelcomeName(null);
    }
  }, [user]);

  const publicRoutePatterns = [
    /^\/signin\/?$/,
    /^\/forgot_password\/?$/,
    /^\/reset_password\/[^/]+\/[^/]+\/?$/,
    /^\/reset-password\/?$/, // legacy dash variant
    /^\/new_password\/[^/]+\/[^/]+\/?$/,
    /^\/new-password\/?$/,
    /^\/admin-login\/?$/,
  ];

  const isPublicPath = publicRoutePatterns.some((regex) => regex.test(location.pathname));

  const handleSignOut = async () => {
    await signOut();
    navigate('/signin');
  };

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  const renderPublicRoutes = () => (
    <Routes>
      <Route path="/signin" element={<SignIn />} />
      <Route path="/forgot_password" element={<ForgotPassword />} />
      <Route path="/reset_password/:token/:hash" element={<ResetPassword />} />
      <Route path="/new_password/:token/:hash" element={<ResetPassword />} />
      {/* Support dashed aliases */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token/:hash" element={<ResetPassword />} />
      <Route path="/new-password/:token/:hash" element={<ResetPassword />} />
      <Route path="/admin-login" element={<AdminLogin />} />
      <Route path="*" element={<Navigate to="/signin" replace />} />
    </Routes>
  );

  if (!user) {
    return (
      <React.Suspense fallback={<LoadingSpinner fullScreen />}>
        {renderPublicRoutes()}
      </React.Suspense>
    );
  }

  if (user && isPublicPath) {
    return <Navigate to="/dashboard" replace />;
  }

  if (user) {
    const isSidebarRoute =
      location.pathname.startsWith('/dashboard') ||
      location.pathname.startsWith('/predictive') ||
      location.pathname === '/';

    // Dashboard & Predictive: sidebar + content (no header)
    if (isSidebarRoute) {
      return (
        <div className="app-wrapper">
          <div className="main-container">
            <Sidebar user={user} />
            <main className="content-area">
              {welcomeName && (
                <div className="alert alert-success" role="status" style={{ margin: '16px' }}>
                  {welcomeName}
                </div>
              )}
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route path="/dashboard/*" element={<Dashboard user={user} />} />
                  <Route path="/predictive/*" element={<Predictive user={user} />} />
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </React.Suspense>
            </main>
          </div>
        </div>
      );
    }

    // Other pages: header + full-width content (no sidebar)
    return (
      <div className="app-wrapper app-wrapper--with-header">
        <Header
          user={user}
          onSignOut={handleSignOut}
          impersonation={impersonation}
          onStopImpersonation={stopImpersonation}
        />
        <main className="content-area content-area--full-width">
          {welcomeName && (
            <div
              className="alert alert-success"
              role="status"
              style={{
                margin: '16px',
                maxWidth: '360px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>{welcomeName}</span>
              <button
                type="button"
                className="close"
                aria-label="Dismiss welcome message"
                onClick={() => setWelcomeName(null)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '18px',
                  lineHeight: 1,
                  cursor: 'pointer',
                  color: 'inherit',
                }}
              >
                ×
              </button>
            </div>
          )}
          <React.Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/validate/activity" element={<ValidationActivity />} />
              <Route path="/submissions" element={<Submissions />} />
              <Route path="/validate" element={<Validate user={user} />} />
              <Route path="/validate/county/:countyId/*" element={<Validate user={user} />} />
              <Route path="/upload/*" element={<ValidationRedirect />} />
              <Route path="/settings/*" element={<Settings user={user} />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </React.Suspense>
        </main>
      </div>
    );
  }

  return (
    <React.Suspense fallback={<LoadingSpinner fullScreen />}>
      <Routes>
        {renderPublicRoutes()}
      </Routes>
    </React.Suspense>
  );
}

export default AppShell;
